/**
 * SecurityLog.jsx
 *
 * Live intruder log — reads from the `security_logs` Firestore collection.
 * Every failed access attempt written by PurgatoryPage shows up here in
 * real time: total count, per-event IP, path, and timestamp.
 *
 * Admin-only: only renders if the current user has role === 'admin'.
 */

import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  limit,
} from 'firebase/firestore';
import { db } from '../../firebase-config';
import { useAuth } from '../../context/AuthContext';

const MAX_ROWS = 50;                    // cap the live feed at 50 most recent
const EVENT_LABEL = 'FULL_NEDRY_LOCKOUT';

function formatTimestamp(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export default function SecurityLog() {
  const { userProfile } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = userProfile?.role === 'admin';

  useEffect(() => {
    // Only admins subscribe to the log
    if (!isAdmin) return;

    const q = query(
      collection(db, 'security_logs'),
      orderBy('timestamp', 'desc'),
      limit(MAX_ROWS)
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error('SecurityLog read error:', err);
      setLoading(false);
    });

    return unsubscribe;
  }, [isAdmin]);

  // Only admins see this panel
  if (!isAdmin) return null;

  const intruderCount = logs.length;

  return (
    <div style={styles.panel}>

      {/* Header row */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.lockIcon}>🔒</span>
          <span style={styles.title}>SECURITY LOG</span>
        </div>
        <div style={styles.badge}>
          <span style={styles.badgeCount}>{intruderCount}</span>
          <span style={styles.badgeLabel}>
            {intruderCount === 1 ? 'INTRUSION' : 'INTRUSIONS'} LOGGED
          </span>
        </div>
      </div>

      {/* Divider */}
      <div style={styles.divider} />

      {/* Table */}
      {loading ? (
        <p style={styles.empty}>Querying security grid...</p>
      ) : logs.length === 0 ? (
        <p style={styles.empty}>No intrusion attempts recorded. Clean slate.</p>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: '28px' }}>#</th>
                <th style={styles.th}>IP ADDRESS</th>
                <th style={styles.th}>PATH HIT</th>
                <th style={styles.th}>EVENT</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>TIMESTAMP</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => (
                <tr key={log.id} style={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                  <td style={styles.tdMuted}>{i + 1}</td>
                  <td style={styles.tdIP}>{log.ip || '—'}</td>
                  <td style={styles.tdPath}>{log.path || '/'}</td>
                  <td style={styles.tdEvent}>{log.event || EVENT_LABEL}</td>
                  <td style={{ ...styles.tdMuted, textAlign: 'right' }}>
                    {formatTimestamp(log.timestamp)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {logs.length === MAX_ROWS && (
        <p style={styles.cap}>Showing {MAX_ROWS} most recent entries.</p>
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  panel: {
    background: '#0d0d0d',
    border: '1px solid #2a0000',
    borderRadius: '8px',
    padding: '20px 24px',
    fontFamily: "'Courier New', Courier, monospace",
    marginTop: '24px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  lockIcon: {
    fontSize: '1.1rem',
  },
  title: {
    color: '#ff3333',
    fontWeight: 700,
    fontSize: '0.8rem',
    letterSpacing: '0.18em',
  },
  badge: {
    background: 'rgba(180, 0, 0, 0.18)',
    border: '1px solid #660000',
    borderRadius: '4px',
    padding: '4px 14px',
    display: 'flex',
    alignItems: 'baseline',
    gap: '6px',
  },
  badgeCount: {
    color: '#ff4444',
    fontWeight: 900,
    fontSize: '1.5rem',
    lineHeight: 1,
  },
  badgeLabel: {
    color: '#aa2222',
    fontSize: '0.65rem',
    letterSpacing: '0.12em',
    fontWeight: 700,
  },
  divider: {
    height: '1px',
    background: 'linear-gradient(90deg, #660000, transparent)',
    marginBottom: '14px',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.75rem',
  },
  th: {
    color: '#660000',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    padding: '6px 10px',
    textAlign: 'left',
    borderBottom: '1px solid #2a0000',
  },
  rowEven: {
    background: 'transparent',
  },
  rowOdd: {
    background: 'rgba(100, 0, 0, 0.06)',
  },
  tdIP: {
    color: '#ff8800',
    padding: '6px 10px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  tdPath: {
    color: '#cc4444',
    padding: '6px 10px',
  },
  tdEvent: {
    color: '#882222',
    padding: '6px 10px',
    fontSize: '0.68rem',
    letterSpacing: '0.08em',
  },
  tdMuted: {
    color: '#553333',
    padding: '6px 10px',
    whiteSpace: 'nowrap',
  },
  empty: {
    color: '#553333',
    fontSize: '0.78rem',
    letterSpacing: '0.08em',
    margin: 0,
  },
  cap: {
    color: '#442222',
    fontSize: '0.65rem',
    marginTop: '10px',
    textAlign: 'right',
    letterSpacing: '0.08em',
  },
};
